import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, Search, Activity, ChevronDown, X, Loader2, CheckCircle, XCircle, AlertCircle, PlusCircle, MinusCircle, Circle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const LiveAffiliateChecker = () => {
  // Brand picker state
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [brandError, setBrandError] = useState(null);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Job config
  const [concurrency, setConcurrency] = useState(10);
  const [maxAffiliates, setMaxAffiliates] = useState(1000);
  const [scanAll, setScanAll] = useState(false);
  const [startFromId, setStartFromId] = useState('');

  // Job state (mirrors backend /status response)
  const [jobStatus, setJobStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [actionError, setActionError] = useState(null);
  const [isActing, setIsActing] = useState(false);

  const dropdownRef = useRef(null);
  const pollRef = useRef(null);
  const logsEndRef = useRef(null);

  // ── Brand list fetch ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setLoadingBrands(true);
        setBrandError(null);
        const r = await fetch(`${API_BASE}/api/live-affiliate-checker/brands`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const j = await r.json();
        if (r.ok && j.success) setBrands(j.data || []);
        else setBrandError(j.message || 'Failed to load brands');
      } catch (e) {
        setBrandError(e.message);
      } finally {
        setLoadingBrands(false);
      }
    })();
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // ── Status polling ──────────────────────────────────────────────────
  const fetchStatus = async () => {
    try {
      const [sRes, lRes] = await Promise.all([
        fetch(`${API_BASE}/api/live-affiliate-checker/status`, { credentials: 'include' }),
        fetch(`${API_BASE}/api/live-affiliate-checker/logs?limit=200`, { credentials: 'include' })
      ]);
      const s = await sRes.json();
      const l = await lRes.json();
      if (s.success) setJobStatus(s.data);
      if (l.success) setLogs(l.data || []);
    } catch (e) {
      // swallow — keep polling
    }
  };

  // Load status once on mount so the UI reflects an in-flight job after refresh
  useEffect(() => { fetchStatus(); }, []);

  // Poll every 2s while a job is running
  useEffect(() => {
    if (jobStatus && jobStatus.running) {
      pollRef.current = setInterval(fetchStatus, 2000);
      return () => clearInterval(pollRef.current);
    }
  }, [jobStatus?.running]);

  // Auto-scroll logs to bottom
  useEffect(() => {
    if (logsEndRef.current) logsEndRef.current.scrollTop = logsEndRef.current.scrollHeight;
  }, [logs.length]);

  // ── Actions ─────────────────────────────────────────────────────────
  const handleStart = async () => {
    if (!selectedBrand || isActing) return;
    setIsActing(true);
    setActionError(null);
    try {
      const r = await fetch(`${API_BASE}/api/live-affiliate-checker/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandUrl: selectedBrand.brand_url,
          brandName: selectedBrand.brand_name,
          concurrency: Number(concurrency),
          maxAffiliates: scanAll ? null : Number(maxAffiliates),
          startFromId: startFromId === '' ? null : Number(startFromId)
        })
      });
      const j = await r.json();
      if (!r.ok || !j.success) {
        setActionError(j.message || j.error || 'Failed to start');
      } else {
        fetchStatus();
      }
    } catch (e) {
      setActionError(e.message);
    } finally {
      setIsActing(false);
    }
  };

  const handleStop = async () => {
    if (isActing) return;
    setIsActing(true);
    setActionError(null);
    try {
      const r = await fetch(`${API_BASE}/api/live-affiliate-checker/stop`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const j = await r.json();
      if (!r.ok || !j.success) setActionError(j.message || j.error || 'Failed to stop');
      fetchStatus();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setIsActing(false);
    }
  };

  // ── Brand picker helpers ────────────────────────────────────────────
  const filteredBrands = (() => {
    const term = search.trim().toLowerCase();
    if (!term) return brands;
    return brands.filter(b =>
      (b.brand_name || '').toLowerCase().includes(term) ||
      (b.brand_url || '').toLowerCase().includes(term)
    );
  })();

  const isRunning = jobStatus && jobStatus.running;
  const hasJob = jobStatus && jobStatus.hasJob;
  const pct = hasJob && jobStatus.total > 0
    ? Math.round((jobStatus.processed / jobStatus.total) * 100)
    : 0;

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Live Affiliate Checker</h1>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              Scans affiliate_clean for pages mentioning the selected brand and appends confirmed matches to live_affiliate_brand_url.
            </p>
          </div>
        </div>

        {/* Config card */}
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Brand</label>

          <div className="relative" ref={dropdownRef}>
            {selectedBrand && !isOpen ? (
              <div
                className="flex items-center justify-between px-3 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-800 cursor-pointer"
                onClick={() => !isRunning && setIsOpen(true)}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                    {selectedBrand.brand_name || '(no name)'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{selectedBrand.brand_url}</div>
                </div>
                {!isRunning && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSelectedBrand(null); }}
                    className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
                  onFocus={() => setIsOpen(true)}
                  placeholder={loadingBrands ? 'Loading brands...' : 'Search brand by name or URL'}
                  disabled={loadingBrands || isRunning}
                  className="w-full pl-9 pr-9 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            )}

            {isOpen && !loadingBrands && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
                {brandError ? (
                  <div className="px-3 py-3 text-sm text-red-600">{brandError}</div>
                ) : filteredBrands.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500 dark:text-slate-400">No brands match.</div>
                ) : (
                  filteredBrands.map((b) => (
                    <button
                      type="button"
                      key={b.brand_url}
                      onClick={() => { setSelectedBrand(b); setSearch(''); setIsOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-slate-700/60 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                        {b.brand_name || '(no name)'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 truncate">{b.brand_url}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Concurrency + max + startFromId */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">Concurrency</label>
              <input
                type="number"
                min="1"
                max="50"
                value={concurrency}
                onChange={(e) => setConcurrency(e.target.value)}
                disabled={isRunning}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm disabled:bg-gray-100 dark:disabled:bg-slate-800/50"
              />
              <div className="text-xs text-gray-400 mt-1">Parallel affiliates per batch (1–50). Default 10.</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">Start from record ID</label>
              <input
                type="number"
                min="1"
                value={startFromId}
                onChange={(e) => setStartFromId(e.target.value)}
                disabled={isRunning}
                placeholder="(blank = from beginning)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm disabled:bg-gray-100 dark:disabled:bg-slate-800/50"
              />
              <div className="text-xs text-gray-400 mt-1">Resume support — skips rows with id below this value.</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300">Max affiliates</label>
                <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={scanAll}
                    onChange={(e) => setScanAll(e.target.checked)}
                    disabled={isRunning}
                    className="rounded border-gray-300"
                  />
                  Scan all
                </label>
              </div>
              <input
                type="number"
                min="1"
                max="100000"
                value={scanAll ? '' : maxAffiliates}
                placeholder={scanAll ? 'all rows in affiliate_clean' : ''}
                onChange={(e) => setMaxAffiliates(e.target.value)}
                disabled={isRunning || scanAll}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm disabled:bg-gray-100 dark:disabled:bg-slate-800/50"
              />
              <div className="text-xs text-gray-400 mt-1">
                {scanAll
                  ? 'No limit — every affiliate in the DB will be checked (~12–15 h at concurrency 10).'
                  : 'Use 100 for spot-check, 1000 for sanity-run. Toggle "Scan all" for a full scan.'}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-5 flex items-center gap-3">
            {!isRunning ? (
              <button
                type="button"
                onClick={handleStart}
                disabled={!selectedBrand || isActing}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg"
              >
                {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Start
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStop}
                disabled={isActing}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg"
              >
                {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
                Stop
              </button>
            )}
            {actionError && (
              <span className="text-sm text-red-600 dark:text-red-400">{actionError}</span>
            )}
          </div>
        </div>

        {/* Progress card */}
        {hasJob && (
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-slate-100">
                  {jobStatus.brandName} <span className="text-gray-400">({jobStatus.brandHost})</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-slate-400">
                  Status: <span className="font-medium">{jobStatus.status}</span>
                  {jobStatus.startedAt && <> · started {new Date(jobStatus.startedAt).toLocaleTimeString()}</>}
                  {jobStatus.completedAt && <> · finished {new Date(jobStatus.completedAt).toLocaleTimeString()}</>}
                  {jobStatus.firstId != null && <> · ids {jobStatus.firstId}..{jobStatus.lastId}</>}
                  {jobStatus.lastProcessedId != null && <> · last id processed <span className="font-mono font-medium">{jobStatus.lastProcessedId}</span></>}
                </div>
              </div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-slate-100">{pct}%</div>
            </div>

            <div className="w-full h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden mb-4">
              <div
                className={`h-full transition-all duration-500 ${isRunning ? 'bg-blue-500' : jobStatus.status === 'error' ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Processed" value={`${jobStatus.processed} / ${jobStatus.total}`} icon={Circle} color="text-gray-600" />
              <Stat label="Added" value={jobStatus.addedLive ?? 0} icon={PlusCircle} color="text-green-600" />
              <Stat label="Still live" value={jobStatus.stillLive ?? 0} icon={CheckCircle} color="text-emerald-600" />
              <Stat label="Removed (lost)" value={jobStatus.removedLost ?? 0} icon={MinusCircle} color="text-orange-600" />
              <Stat label="Not present" value={jobStatus.notPresent ?? 0} icon={XCircle} color="text-gray-500" />
              <Stat label="Errors" value={jobStatus.errors ?? 0} icon={AlertCircle} color="text-red-600" />
            </div>
          </div>
        )}

        {/* Logs */}
        {hasJob && (
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl p-5 shadow-sm">
            <div className="text-sm font-medium text-gray-900 dark:text-slate-100 mb-2">Activity log</div>
            <div
              ref={logsEndRef}
              className="font-mono text-xs bg-gray-900 text-gray-200 rounded-lg p-3 h-72 overflow-y-auto"
            >
              {logs.length === 0 ? (
                <div className="text-gray-500">No log entries yet.</div>
              ) : (
                logs.map((entry, i) => (
                  <div key={i} className="whitespace-pre-wrap">
                    <span className="text-gray-500">[{new Date(entry.ts).toLocaleTimeString()}]</span>{' '}
                    <span className={
                      entry.msg.includes(' ADDED ') ? 'text-green-400' :
                      entry.msg.includes(' REMOVED ') ? 'text-orange-400' :
                      entry.msg.includes(' still live ') ? 'text-emerald-300' :
                      entry.msg.includes(' not present ') ? 'text-gray-400' :
                      entry.msg.includes('ERROR') || entry.msg.includes('FATAL') ? 'text-red-400' :
                      'text-blue-300'
                    }>{entry.msg}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-3">
    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400 mb-1">
      {Icon && <Icon className={`w-3.5 h-3.5 ${color || ''}`} />}
      {label}
    </div>
    <div className={`text-lg font-semibold ${color || 'text-gray-900 dark:text-slate-100'}`}>{value}</div>
  </div>
);

export default LiveAffiliateChecker;
